import { Center, Flex, Group, Loader, Paper, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Client } from 'src/admin/users/types';
import { useClients } from 'src/clients/hooks/useClients';
import { Family, useFamily } from 'src/clients/hooks/useFamily';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { FamilyIndividualToggle } from '../FamilyIndividualToggle';
import { SquareIconButton } from '../SquareIconButton';
import { FamilyMember } from './useFamilyMembers';
import { FamilyProfilePanel } from './FamilyProfileContent';

const FAMILY_TABS = ['profile', 'financial', 'cashflows', 'documents', 'meetings'] as const;
type FamilyTab = (typeof FAMILY_TABS)[number];

export function FamilyData() {
  const { familyId = '', tab } = useParams<{ familyId: string; tab?: string }>();
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activeTab: FamilyTab = FAMILY_TABS.includes(tab as FamilyTab)
    ? (tab as FamilyTab)
    : 'profile';

  const mainMemberClientId = family?.mainFamilyMember.clientId;
  const memberParam = searchParams.get('member');

  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const members: FamilyMember[] = buildMembers(family, clientById);

  // Trust the ?member param as the came-from client: it is set by our own
  // navigation. Validating it against the family-filtered clients query would
  // fall back to the main member while that query is still loading (the family
  // is often cached and resolves first), losing the client the user came from.
  // Cold deep-links without ?member default to the main family member.
  const contextClientId = memberParam ?? mainMemberClientId ?? null;

  const contextClient = contextClientId ? clientById[contextClientId] : null;
  const contextClientName = contextClient
    ? formatName(contextClient.firstName, contextClient.lastName, NameFormat.NATURAL)
    : 'Individual';

  const familyLabel = family?.name ?? 'Family';

  const hasOtherMembers = members.some(m => m.clientId !== contextClientId);

  function handleTabChange(nextTab: string) {
    const query = searchParams.toString();
    navigate(`/family/${familyId}/${nextTab}${query ? `?${query}` : ''}`);
  }

  function handleToggleChange(value: 'family' | 'individual') {
    if (value === 'individual' && contextClientId) {
      navigate(`/client/${contextClientId}/non-financial-data`);
    }
  }

  function handleMemberSelect(memberId: string) {
    navigate(`/client/${memberId}/non-financial-data`);
  }

  const toggle = (
    <FamilyIndividualToggle
      variant="profile"
      familyScreenEnabled
      value="family"
      onChange={handleToggleChange}
      familyLabel={familyLabel}
      individualLabel={contextClientName}
      members={hasOtherMembers ? members : undefined}
      onMemberSelect={hasOtherMembers ? handleMemberSelect : undefined}
    />
  );

  const tabs = [
    { label: 'Profile', value: 'profile' },
    { label: 'Financial', value: 'financial' },
    { label: 'Cashflows', value: 'cashflows' },
    { label: 'Documents', value: 'documents' },
    { label: 'Meetings', value: 'meetings' },
  ];

  const backButton = (
    <SquareIconButton
      diameter={48}
      radius={20}
      aria-label="Back to families"
      onClick={() => navigate('/clients/families')}
    >
      <IconArrowLeft size={24} color="var(--mantine-color-gray-9)" />
    </SquareIconButton>
  );

  if (isFamilyLoading) {
    return (
      <Flex direction="column" gap="lg" pos="relative">
        <UnderlineTabs
          mode="local"
          routerTabsV2
          tabs={tabs}
          value={activeTab}
          onChange={handleTabChange}
          leadingContent={
            <Group gap="sm" wrap="nowrap">
              {backButton}
            </Group>
          }
        />
        <Paper variant="content">
          <Center py="xxl">
            <Loader />
          </Center>
        </Paper>
      </Flex>
    );
  }

  if (!family) {
    return (
      <Flex direction="column" gap="lg" pos="relative">
        <Group gap="sm" wrap="nowrap">
          {backButton}
        </Group>
        <Paper variant="content">
          <Text c="gray.6">This family could not be found.</Text>
        </Paper>
      </Flex>
    );
  }

  let content: React.ReactNode;
  if (activeTab === 'profile') {
    content = <FamilyProfilePanel familyId={familyId} />;
  } else {
    content = (
      <Paper variant="content">
        <Center py="xxl">
          <Text c="gray.6">Coming soon</Text>
        </Center>
      </Paper>
    );
  }

  return (
    <Flex direction="column" gap="lg" pos="relative">
      <UnderlineTabs
        mode="local"
        routerTabsV2
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        leadingContent={
          <Group gap="sm" wrap="nowrap">
            {backButton}
            {toggle}
          </Group>
        }
      />
      {content}
    </Flex>
  );
}

function buildMembers(family: Family | null, clientById: Record<string, Client>): FamilyMember[] {
  if (!family) return [];

  const mainClientId = family.mainFamilyMember.clientId;
  const secondaryIds = family.secondaryFamilyMembers.map(m => m.clientId);

  const mainClient = clientById[mainClientId];
  const mainMember: FamilyMember[] = mainClient
    ? [{ clientId: mainClientId, client: mainClient, isMain: true }]
    : [];

  const secondaryMembers: FamilyMember[] = secondaryIds
    .map(id => {
      const c = clientById[id];
      if (!c) return null;
      return { clientId: id, client: c, isMain: false };
    })
    .filter((m): m is FamilyMember => m !== null)
    .sort((a, b) => {
      const aName = `${a.client.firstName} ${a.client.lastName}`.toLowerCase();
      const bName = `${b.client.firstName} ${b.client.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });

  return [...mainMember, ...secondaryMembers];
}
